import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Beaker,
  Calendar as CalendarIcon,
  Download,
  ShoppingCart,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  Copy,
  BookOpen,
  Share2,
  Mail,
  Printer,
  Crown,
  Save
} from "lucide-react";
import { addDays, format } from "date-fns";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { PeptidesTab } from "@/components/results/PeptidesTab";
import { BeginnerGuideTab } from "@/components/results/BeginnerGuideTab";
import { ScheduleTab } from "@/components/results/ScheduleTab";
import { BuyTab } from "@/components/results/BuyTab";
import { StatsOverview } from "@/components/results/StatsOverview";
import { exportCycleChecklistPDF, exportProtocolPDF } from "@/utils/pdfExport";
import { CollaboratorsList } from "@/components/results/CollaboratorsList";
import { tr } from "@/utils/i18n";

export default function ResultsPage() {
  const navigate = useNavigate();
  const { getAuthHeader, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const [cycleData, setCycleData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startDate] = useState(new Date());
  const [exporting, setExporting] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [sharePermission, setSharePermission] = useState("view");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [collabRefresh, setCollabRefresh] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const printRef = useRef(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await api.get('/subscription/status', { headers: getAuthHeader() });
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.warn('Subscription status fetch failed:', err?.response?.status || err?.message);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    const stored = localStorage.getItem("cycleResults");
    if (stored) {
      setCycleData(JSON.parse(stored));
    } else {
      navigate("/");
      toast.error("No cycle data found. Please generate a cycle first.");
    }
    if (isAuthenticated) fetchSubscriptionStatus();
  }, [navigate, isAuthenticated, fetchSubscriptionStatus]);

  const isPremium = subscriptionStatus?.is_premium;

  const handleShareCycle = async () => {
    if (!isPremium) {
      toast.error(tr(language,
        "Premium feature - Upgrade to share cycles",
        "Recurso Premium - Faça upgrade para compartilhar",
        "Función Premium - Actualice para compartir"));
      navigate("/subscription");
      return;
    }
    if (!shareEmail) {
      toast.error(tr(language, "Please enter an email", "Digite um email", "Ingrese un email"));
      return;
    }
    setShareLoading(true);
    try {
      const response = await api.post(`/cycles/${cycleData.id}/share`, {
        cycle_id: cycleData.id,
        email: shareEmail,
        permission: sharePermission
      }, { headers: getAuthHeader() });
      setShareLink(response.data.share_link);
      setCollabRefresh(k => k + 1);
      setShareEmail("");
      toast.success(tr(language,
        "Cycle shared successfully!",
        "Ciclo compartilhado com sucesso!",
        "¡Ciclo compartido con éxito!"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to share cycle");
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success(tr(language, "Link copied!", "Link copiado!", "¡Enlace copiado!"));
  };

  const handleExportPDFWithChecklist = async () => {
    if (!cycleData) return;
    setExporting(true);
    toast.info(tr(language, "Generating PDF...", "Gerando PDF...", "Generando PDF..."));
    try {
      exportCycleChecklistPDF(cycleData);
      toast.success(tr(language,
        "PDF exported successfully!",
        "PDF exportado com sucesso!",
        "¡PDF exportado con éxito!"));
    } catch {
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!cycleData) return;
    setExporting(true);
    toast.info("Generating comprehensive PDF...");
    try {
      exportProtocolPDF(cycleData);
      toast.success("PDF exported successfully!");
    } catch {
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => window.print();

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const trackAffiliateClick = async (vendor) => {
    try {
      await api.post(`/affiliate/track?vendor=${vendor}&cycle_id=${cycleData?.id || ''}`, {}, {
        headers: getAuthHeader()
      });
    } catch (err) {
      // Non-blocking: click tracking should never interrupt navigation
      console.warn('Affiliate click tracking failed:', err?.message);
    }
  };

  const handleAffiliateClick = (vendor, url, opts = {}) => {
    trackAffiliateClick(vendor);
    // When called from an <a> onClick, the anchor handles navigation itself.
    // We only need to track. Callers pass { trackOnly: true } or a null URL.
    if (opts.trackOnly || !url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getDaySchedule = (date) => {
    if (!cycleData?.weekly_schedule) return [];
    const dayName = format(date, 'EEEE');
    return cycleData.weekly_schedule[dayName] || [];
  };

  const isDateInCycle = (date) => {
    const endDate = addDays(startDate, (cycleData?.total_duration_weeks || 8) * 7);
    return date >= startDate && date <= endDate;
  };

  const handleSaveCycle = async () => {
    if (!isAuthenticated) {
      toast.error(tr(language,
        "Please login to save",
        "Faça login para salvar",
        "Inicie sesión para guardar"));
      return;
    }
    try {
      await api.post('/dashboard/cycles', cycleData, { headers: getAuthHeader() });
      toast.success(t("cycleSaved"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save cycle");
    }
  };

  if (!cycleData) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  const beginnerGuide = cycleData.beginner_guide || {};

  return (
    <div className="min-h-screen bg-[#050505] grid-pattern">
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-zinc-400 hover:text-white"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">{t("yourPersonalized")} {t("cycleProtocol")}</h1>
                <p className="text-sm text-zinc-400">{t("goal")}: {cycleData.goal}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />

              <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:text-white" data-testid="share-btn">
                    <Share2 className="w-4 h-4 mr-2" />
                    {tr(language, "Share", "Compartilhar", "Compartir")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-950 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                      <Share2 className="w-5 h-5 text-cyan-400" />
                      {tr(language, "Share Cycle", "Compartilhar Ciclo", "Compartir Ciclo")}
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400">
                      {tr(language,
                       "Send your cycle to a friend or collaborator",
                       "Envie seu ciclo para um amigo ou colaborador",
                       "Envía tu ciclo a un amigo o colaborador")}
                    </DialogDescription>
                  </DialogHeader>

                  {!isPremium && (
                    <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
                      <div className="flex items-center gap-2 text-amber-400 mb-2">
                        <Crown className="w-4 h-4" />
                        <span className="font-medium">
                          {tr(language, "Premium Feature", "Recurso Premium", "Función Premium")}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-400 mb-3">
                        {tr(language,
                         "Upgrade to share cycles with others",
                         "Faça upgrade para compartilhar ciclos",
                         "Actualice para compartir ciclos")}
                      </p>
                      <Button onClick={() => navigate("/subscription")} className="btn-primary w-full">
                        {tr(language, "View Plans", "Ver Planos", "Ver Planes")}
                      </Button>
                    </div>
                  )}

                  {isPremium && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">
                          {tr(language, "Recipient's email", "Email do destinatário", "Email del destinatario")}
                        </Label>
                        <Input
                          type="email"
                          placeholder="friend@example.com"
                          value={shareEmail}
                          onChange={(e) => setShareEmail(e.target.value)}
                          className="bg-zinc-900 border-zinc-800"
                          data-testid="share-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">
                          {tr(language, "Permission", "Permissão", "Permiso")}
                        </Label>
                        <Select value={sharePermission} onValueChange={setSharePermission}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="share-permission-trigger"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-zinc-800">
                            <SelectItem value="view">{tr(language, "View only", "Apenas visualizar", "Solo ver")}</SelectItem>
                            <SelectItem value="edit">{tr(language, "Can edit", "Pode editar", "Puede editar")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleShareCycle} className="w-full btn-primary" disabled={shareLoading} data-testid="share-send-button">
                        {shareLoading ? (
                          <span className="flex items-center gap-2">
                            <div className="spinner w-4 h-4" />
                            {tr(language, "Sharing...", "Compartilhando...", "Compartiendo...")}
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            {tr(language, "Send Invite", "Enviar Convite", "Enviar Invitación")}
                          </span>
                        )}
                      </Button>
                      {shareLink && (
                        <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                          <p className="text-xs text-cyan-400 mb-2">
                            {tr(language, "Share link:", "Link de compartilhamento:", "Enlace para compartir:")}
                          </p>
                          <div className="flex items-center gap-2">
                            <Input value={shareLink} readOnly className="bg-zinc-900 border-zinc-800 text-xs" data-testid="share-link-input" />
                            <Button size="sm" variant="outline" onClick={copyShareLink} data-testid="copy-share-link-button"><Copy className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      )}

                      {cycleData?.id && (
                        <CollaboratorsList
                          cycleId={cycleData.id}
                          getAuthHeader={getAuthHeader}
                          language={language}
                          refreshKey={collabRefresh}
                        />
                      )}
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              <Button
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:text-white"
                onClick={handleExportPDFWithChecklist}
                disabled={exporting}
                data-testid="export-pdf-btn"
              >
                {exporting ? (
                  <span className="flex items-center gap-2"><div className="spinner w-4 h-4" /></span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">PDF</span>
                  </span>
                )}
              </Button>

              <Button
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:text-white"
                onClick={handleExportPDF}
                disabled={exporting}
                data-testid="export-protocol-pdf-btn"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Protocol</span>
              </Button>

              <Button
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:text-white"
                onClick={handlePrint}
                data-testid="print-btn"
              >
                <Printer className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Print</span>
              </Button>

              <Button className="btn-primary" onClick={handleSaveCycle} data-testid="save-cycle-btn">
                <Save className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">{t("saveCycle")}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" ref={printRef}>
        <div className="mb-6">
          <LanguageSwitcher variant="card" />
        </div>

        <section className="mb-8" data-testid="summary-section">
          {cycleData._fallback && (
            <Card className="bg-amber-500/10 border-amber-500/40 mb-4" data-testid="fallback-banner">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-300 font-semibold">Showing a sample protocol</p>
                    <p className="text-sm text-zinc-400 mt-1">
                      {cycleData._notice || "AI service is unavailable. Showing a sample protocol — personalized generation will resume once the AI key is restored."}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="bg-gradient-to-br from-zinc-900 to-black border-cyan-500/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-white mb-2">AI Recommendation Summary</h2>
                  <p className="text-zinc-400">{cycleData.summary}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <StatsOverview cycleData={cycleData} language={language} t={t} />

        <Tabs defaultValue="peptides" className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1 flex-wrap h-auto">
            <TabsTrigger value="peptides" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="tab-peptides">
              <Beaker className="w-4 h-4 mr-2" />
              {t("peptideProtocol")}
            </TabsTrigger>
            <TabsTrigger value="beginner" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="tab-beginner">
              <BookOpen className="w-4 h-4 mr-2" />
              {t("beginnerGuide")}
            </TabsTrigger>
            <TabsTrigger value="schedule" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="tab-schedule">
              <CalendarIcon className="w-4 h-4 mr-2" />
              {t("weeklySchedule")}
            </TabsTrigger>
            <TabsTrigger value="buy" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="tab-buy">
              <ShoppingCart className="w-4 h-4 mr-2" />
              {t("purchaseLinks")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="peptides">
            <PeptidesTab peptides={cycleData.peptides} />
          </TabsContent>

          <TabsContent value="beginner">
            <BeginnerGuideTab beginnerGuide={beginnerGuide} />
          </TabsContent>

          <TabsContent value="schedule">
            <ScheduleTab
              cycleData={cycleData}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              startDate={startDate}
              getDaySchedule={getDaySchedule}
              isDateInCycle={isDateInCycle}
              copyToClipboard={copyToClipboard}
            />
          </TabsContent>

          <TabsContent value="buy">
            <BuyTab
              cycleData={cycleData}
              handleAffiliateClick={handleAffiliateClick}
              isPremium={isPremium}
              onUpgradeClick={() => navigate('/subscription')}
            />
          </TabsContent>
        </Tabs>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <Card className="bg-zinc-950/50 border-green-500/20">
            <CardContent className="p-6">
              <h3 className="text-white flex items-center gap-2 mb-4 font-semibold">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Tips for Success
              </h3>
              <ul className="space-y-3">
                {cycleData.tips?.map((tip, index) => (
                  <li key={`tip-${index}-${tip.slice(0, 20)}`} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs text-green-400">{index + 1}</span>
                    </div>
                    <p className="text-zinc-300">{tip}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-zinc-950/50 border-red-500/20">
            <CardContent className="p-6">
              <h3 className="text-white flex items-center gap-2 mb-4 font-semibold">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Important Warnings
              </h3>
              <ul className="space-y-3">
                {cycleData.warnings?.map((warning, index) => (
                  <li key={`warn-${index}-${warning.slice(0, 20)}`} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <AlertTriangle className="w-3 h-3 text-red-400" />
                    </div>
                    <p className="text-zinc-300">{warning}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        <div className="mt-8 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800 text-center">
          <p className="text-xs text-zinc-500">
            <strong className="text-zinc-400">Disclaimer:</strong> This information is for research and educational purposes only.
            Peptides are sold strictly for research purposes. Always consult with a qualified healthcare professional before
            starting any new supplement or research compound. Individual results may vary.
          </p>
        </div>
      </main>

      <footer className="py-8 px-4 border-t border-zinc-800 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-zinc-500 text-sm">
            © 2025 PeptidePlanner AI. For research and educational purposes only.
          </p>
        </div>
      </footer>
    </div>
  );
}
